/**
 * Is the caret inside a SQL comment?
 *
 * Completions must not fire inside `-- …` or `/* … *\/`. Beyond being noise,
 * an open suggest widget makes Enter *accept a suggestion* instead of inserting
 * a newline, so typing
 *
 *     -- note⏎BEGIN NULL; END;
 *
 * silently produced `-- noneditionableBEGIN NULL; END;` — the comment rewritten
 * and the line break swallowed. Nothing downstream can undo that, so the gate
 * belongs at the orchestrator's entry point.
 *
 * A single left-to-right scan of the text before the caret, tracking the one
 * piece of state that matters: what construct we are currently inside. Quote
 * tracking is what keeps `SELECT '-- not a comment'` and `WHERE path = '/*'`
 * from reading as comments — a naive `indexOf('--')` would suppress completions
 * for the rest of those statements.
 *
 * Engine-agnostic base: `--` line comments and `/* *\/` block comments are
 * common to every dialect this app speaks. MySQL's `#` line comment is opt-in
 * via `hashIsLineComment` — it must NOT apply to other dialects, where `#`
 * carries real mid-statement meaning: PostgreSQL's JSON/geometric operators
 * (`#>`, `#>>`, `#-`) and MSSQL temp tables (`FROM #temp`) would otherwise
 * read the rest of the statement as a comment and suppress completions.
 */

type ScanState = 'code' | 'single' | 'double' | 'backtick' | 'line-comment' | 'block-comment';

/**
 * True when `textBeforeCaret` ends inside a comment.
 *
 * Takes the raw prefix rather than a Monaco model so it stays unit-testable and
 * free of any editor dependency.
 */
export function isCaretInComment(
  textBeforeCaret: string,
  opts?: { readonly hashIsLineComment?: boolean },
): boolean {
  const hashIsLineComment = opts?.hashIsLineComment ?? false;
  let state: ScanState = 'code';

  for (let i = 0; i < textBeforeCaret.length; i += 1) {
    const ch = textBeforeCaret[i];
    const next = textBeforeCaret[i + 1];

    switch (state) {
      case 'code':
        if (ch === '-' && next === '-') {
          state = 'line-comment';
          i += 1;
        } else if (ch === '#' && hashIsLineComment) {
          state = 'line-comment';
        } else if (ch === '/' && next === '*') {
          state = 'block-comment';
          i += 1;
        } else if (ch === "'") {
          state = 'single';
        } else if (ch === '"') {
          state = 'double';
        } else if (ch === '`') {
          state = 'backtick';
        }
        break;

      // A doubled quote inside a literal ('' / "" / ``) is an escaped quote,
      // not a close. Falling back to `code` and immediately re-entering on the
      // second quote character handles that without a special case.
      case 'single':
        if (ch === "'") state = 'code';
        break;
      case 'double':
        if (ch === '"') state = 'code';
        break;
      case 'backtick':
        if (ch === '`') state = 'code';
        break;

      case 'line-comment':
        if (ch === '\n') state = 'code';
        break;

      case 'block-comment':
        if (ch === '*' && next === '/') {
          state = 'code';
          i += 1;
        }
        break;
    }
  }

  return state === 'line-comment' || state === 'block-comment';
}
